import React, { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import type {
  ModalFooterActionProps,
} from 'LuxKit/Modal/ModalFooter'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import noop from 'lib/function/noop'
import DateRangePicker from 'tripPlanner/Tripkit/Common/DateRangePicker'

interface Props {
  title?: string
  initialStartDate?: moment.Moment
  initialEndDate?: moment.Moment
  onChange: (startDate: moment.Moment, endDate?: moment.Moment) => void
  onRemove?: () => void
  onCancel?: () => void
  onBack?: () => void
  isSubmitting?: boolean
  errorMsg?: string
  onRequestClearError?: () => void
  doneLabel?: string
  showTripDates?: boolean
  startLabel?: string
  endLabel?: string
}

function MobileSelectDates(props: Props) {
  const intl = useIntl()

  const addDatesLabel = intl.formatMessage({
    id: 'GSKLPvg/6V',
    defaultMessage: 'Add dates',
    description: 'Default title and done label for the mobile date selection modal',
  })

  const {
    title = addDatesLabel,
    initialStartDate,
    initialEndDate,
    onChange,
    onRemove,
    onCancel,
    onBack,
    isSubmitting,
    errorMsg,
    onRequestClearError = noop,
    doneLabel = addDatesLabel,
    showTripDates,
    startLabel,
    endLabel,
  } = props

  const [startDate, setStartDate] = useState<moment.Moment | undefined>(
    initialStartDate,
  )
  const [endDate, setEndDate] = useState<moment.Moment | undefined>(
    initialEndDate,
  )

  const [showNoDatesError, setShowNoDatesError] = useState(false)

  const errorToShow =
    errorMsg || (showNoDatesError ? intl.formatMessage({
      id: 'XaeQGiUwkz',
      defaultMessage: 'Please select dates',
      description: 'Error message when user tries to submit without selecting dates',
    }) : undefined)

  const onUpdate = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      if (startDate) {
        setShowNoDatesError(false)
        onChange(startDate, endDate)
      } else {
        setShowNoDatesError(true)
      }
    },
    [startDate, onChange, endDate],
  )

  const onClickRemove = useCallback(() => {
    setShowNoDatesError(false)
    onRemove?.()
  }, [onRemove])

  const onDatesChange = useCallback(
    (dates: { startDate: moment.Moment, endDate?: moment.Moment }) => {
      setStartDate(dates.startDate)
      setEndDate(dates.endDate)
      onRequestClearError()
    },
    [onRequestClearError],
  )

  const onClickCancel = useCallback(() => {
    onCancel?.()
  }, [onCancel])

  const clearDatesActionProps: ModalFooterActionProps = {
    children: intl.formatMessage({
      id: 'EliPVq3bbz',
      defaultMessage: 'Clear dates',
      description: 'Button to clear selected dates in mobile date picker',
    }),
    onClick: onClickRemove,
    disabled: isSubmitting,
  }

  const cancelActionProps: ModalFooterActionProps = {
    children: intl.formatMessage({
      id: 'oFtzQVzXeN',
      defaultMessage: 'Cancel',
      description: 'Button to cancel date selection in mobile date picker',
    }),
    onClick: onClickCancel,
    disabled: isSubmitting,
  }

  return (
    <form onSubmit={onUpdate}>
      <ModalHeader
        title={title}
        onBackButtonClick={onBack}
        onCloseButtonClick={onCancel}
      />
      <ModalBody>
        <ModalContent>
          <VerticalSpacer gap={16}>
            <DateRangePicker
              onDatesChange={onDatesChange}
              startDate={startDate}
              endDate={endDate}
              allowSameDay
              showTripDates={showTripDates}
              startLabel={startLabel}
              endLabel={endLabel}
            />
            {errorToShow && (
              <MessageBanner kind="critical">{errorToShow}</MessageBanner>
            )}
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: doneLabel,
          type: 'submit',
          disabled: isSubmitting,
        }}
        secondaryActionProps={
          onRemove ? clearDatesActionProps : cancelActionProps
        }
        isSecondaryActionHidden={onRemove && !startDate}
      />
    </form>
  )
}

export default MobileSelectDates
